
enum HTMLInjectionPoint {
	HeadEnd = 'head_end',
	BodyEnd = 'body_end',
}
interface Cookie {
	duration: string;
}
interface CookieCategory {
	name: string;
	description: string;
	mandatory: boolean;
}
type CookieCategories = Record<string, CookieCategory>;

interface CookieProvider {
	name: string;
	categories: string[];
	description: string;
	cookies_dropped: Record<string, Cookie>;
	more_information?: string;
	html_injection_point?: HTMLInjectionPoint;
	html?: string;
}
type CookieProviders = Record<string, CookieProvider>;

export interface ConsentContext {
	getProviders(): CookieProviders;
	getPreferences(): string[];
	getCategories(): CookieCategories;
}

export interface ConsentPlugin {
	onConsentChanged( context: ConsentContext ): void;
}

declare global {
	interface Window {
		ten4_cookie_consent?: string[];
	}
}

export class CookieConsentManager {

	categories: CookieCategories;
	providers: CookieProviders;
	provider_hash: string;
	use_cookie_banner: boolean;
	plugins: ConsentPlugin[] = [];

	constructor(
		categories: CookieCategories,
		providers: CookieProviders,
		provider_hash: string,
		use_cookie_banner = false,
		plugins: ConsentPlugin[] = [],
	) {
		this.categories = categories;
		this.providers = providers;
		this.provider_hash = provider_hash;
		this.use_cookie_banner = use_cookie_banner;

		for ( const plugin of plugins ) {
			this.registerPlugin( plugin );
		}

		window.addEventListener( 'click', ( event ) => {
			if ( !( event.target instanceof HTMLElement ) ) {
				return;
			}
			if ( 'cookieManagerAcceptAll' in event.target.dataset ) {
				event.preventDefault();
				this.savePreferences( Object.keys( this.providers ) );
				this.closeCookieManagerUi();
				this.closeCookieBannerUi();
				this.applySavedPreferences();
				this.setInputsFromPreferences();
				this.hideButtons();
				return;
			}
			if ( 'cookieManagerDenyAll' in event.target.dataset ) {
				event.preventDefault();
				this.savePreferences( [] );
				this.closeCookieManagerUi();
				this.closeCookieBannerUi();
				this.applySavedPreferences();
				this.setInputsFromPreferences();
				this.hideButtons();
				return;
			}
			if ( 'cookieManagerAcceptAllCategory' in event.target.dataset ) {
				event.preventDefault();
				const category = event.target.dataset.cookieManagerAcceptAllCategory;
				if ( !category ) {
					return;
				}
				const prefs = ( this.getSavedPreferences() ?? [] ).concat(
					this.getProviderKeysInCategory( category )
				).filter( ( value, index, self ) => {
					// Filter out non-unique items.
					return self.indexOf( value ) === index;
				} );
				this.savePreferences( prefs );
				this.applySavedPreferences();
				this.setInputsFromPreferences();
				this.hideButtons();
				return;
			}
			if ( 'cookieManagerDenyAllCategory' in event.target.dataset ) {
				event.preventDefault();
				const category = event.target.dataset.cookieManagerDenyAllCategory;
				if ( !category ) {
					return;
				}
				const category_prefs = this.getProviderKeysInCategory( category );
				const prefs = ( this.getSavedPreferences() ?? [] ).filter(
					( value ) => {
						return !category_prefs.includes( value );
					}
				);
				this.savePreferences( prefs );
				this.applySavedPreferences();
				this.setInputsFromPreferences();
				this.hideButtons();
				return;
			}
			if ( 'cookieManagerAcceptProvider' in event.target.dataset ) {
				event.preventDefault();
				const provider = event.target.dataset.cookieManagerAcceptProvider;
				if ( !provider ) {
					return;
				}
				const prefs = ( this.getSavedPreferences() ?? [] ).concat(
					[provider]
				).filter( ( value, index, self ) => {
					// Filter out non-unique items.
					return self.indexOf( value ) === index;
				} );
				this.savePreferences( prefs );
				this.applySavedPreferences();
				this.setInputsFromPreferences();
				this.hideButtons();
				return;
			}
			if ( 'cookieManagerSaveChanges' in event.target.dataset ) {
				event.preventDefault();
				this.savePreferences( this.getPreferencesFromInputs() );
				this.closeCookieManagerUi();
				this.applySavedPreferences();
				this.hideButtons();
				return;
			}
			if ( 'cookieManagerOpenUi' in event.target.dataset ) {
				event.preventDefault();
				this.openCookieManagerUi();
				const manager_el = document.querySelector( '.cookie-manager' ) as HTMLElement | null;
				if ( manager_el ) {
					this.trapFocus( manager_el );
				}
				return;
			}
		} );

		window.addEventListener( 'change', ( event ) => {
			if ( !( event.target instanceof HTMLElement ) ) {
				return;
			}
			if (
				'cookieManagerCategoryPreference' in event.target.dataset
				|| 'cookieManagerProviderPreference' in event.target.dataset
			) {
				this.showButtons();
				this.forceSaveChanges();
			}
		} );

		const cookie_preferences = this.getSavedPreferences();
		if ( cookie_preferences === null ) {
			this.showButtons();
			if ( use_cookie_banner ) {
				this.openCookieBannerUi();
				const banner = document.querySelector( '.cookie-banner' ) as HTMLElement | null;
				if ( banner ) {
					this.trapFocus( banner );
				}
			} else {
				this.openCookieManagerUi();
				const manager_el = document.querySelector( '.cookie-manager' ) as HTMLElement | null;
				if ( manager_el ) {
					this.trapFocus( manager_el );
				}
			}
			return;
		}

		this.applySavedPreferences();
		this.setInputsFromPreferences();
	}

	registerPlugin( plugin: ConsentPlugin ): void {
		this.plugins.push( plugin );

		const context = this.getConsentContext();
		plugin.onConsentChanged( context );
	}

	private getConsentContext(): ConsentContext {
		const manager = this;

		return {
			getPreferences(): string[] {
				return manager.getSavedPreferences() ?? [];
			},
			getCategories(): CookieCategories {
				return manager.categories;
			},
			getProviders(): CookieProviders {
				return manager.providers;
			},
		};
	}

	private notifyPlugins(): void {
		if ( this.plugins.length === 0 ) {
			return;
		}
		const context = this.getConsentContext();
		for ( const plugin of this.plugins ) {
			plugin.onConsentChanged( context );
		}
	}

	savePreferences( agreed_providers: string[] ): void {
		const date = new Date();
		date.setTime( date.getTime() + ( 90 * 24 * 60 * 60 * 1000 ) );

		const validated_agreed_providers = [];
		for ( const provider_key of agreed_providers ) {
			if ( !Object.prototype.hasOwnProperty.call(
				this.providers,
				provider_key
			) ) {
				continue;
			}
			if ( this.getProviderIsMandatory( provider_key ) ) {
				continue;
			}
			validated_agreed_providers.push( provider_key );
		}

		this.setCookie(
			'ten4_cookie_consent',
			this.provider_hash + '|' + validated_agreed_providers.join( ',' ),
			date
		);
	}

	getSavedPreferences(): null|string[] {
		const consent_cookie = this.getCookie( 'ten4_cookie_consent' );
		if ( consent_cookie !== '' ) {
			const [hash, agreed_providers] = consent_cookie.split( '|' );
			if ( hash === this.provider_hash ) {
				if ( agreed_providers === '' ) {
					return [];
				}
				return agreed_providers.split( ',' );
			}
			return null;
		}
		return null;
	}

	getPreferencesFromInputs(): string[] {
		let preferences: string[] = [];
		const category_pref_inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
			'[data-cookie-manager-category-preference]'
		);
		for ( const input of category_pref_inputs ) {
			if ( !input.checked ) {
				continue;
			}
			if ( !input.dataset.cookieManagerCategoryPreference ) {
				continue;
			}
			preferences = preferences.concat(
				this.getProviderKeysInCategory(
					input.dataset.cookieManagerCategoryPreference
				)
			);
		}
		const provider_pref_inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
			'[data-cookie-manager-provider-preference]'
		);
		for ( const input of provider_pref_inputs ) {
			if ( !input.checked ) {
				continue;
			}
			if ( !input.dataset.cookieManagerProviderPreference ) {
				continue;
			}
			preferences.push( input.dataset.cookieManagerProviderPreference );
		}

		return preferences.filter( ( value, index, self ) => {
			// Filter out non-unique items.
			return self.indexOf( value ) === index;
		} );
	}

	setInputsFromPreferences(): void {
		const preferences = this.getSavedPreferences() ?? [];
		const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
			'[data-cookie-manager-provider-preference]'
		);
		let updated = false;
		for ( const input of inputs ) {
			if ( !input.dataset.cookieManagerProviderPreference ) {
				continue;
			}
			if (
				!preferences.includes(
					input.dataset.cookieManagerProviderPreference
				)
			) {
				input.checked = false;
				continue;
			}
			input.checked = true;
			updated = true;
		}
		if ( updated ) {
			this.forceSaveChanges();
		}
	}

	applySavedPreferences(): void {
		const agreed_provider_keys = this.getSavedPreferences();

		// Set global variable for e.g. tag managers to use.
		window.ten4_cookie_consent = agreed_provider_keys ?? [];

		if ( agreed_provider_keys === null ) {
			return;
		}
		const agreed_providers: Record<string, CookieProvider> = {};
		const denied_providers: Record<string, CookieProvider> = {};
		outer: for ( const provider in this.providers ) {
			if (
				!agreed_provider_keys.includes( provider )
			) {
				denied_providers[provider] = this.providers[provider];
				continue outer;
			}
			agreed_providers[provider] = this.providers[provider];
		}

		this.removeProviderCookies( denied_providers );
		this.injectHtml( Object.values( agreed_providers ) );
		this.renderProviderWrappers( agreed_providers );

		this.notifyPlugins();
	}

	getProviderIsMandatory( provider_key: string ): boolean {
		const categories = this.providers[provider_key].categories;
		for ( const category of categories ) {
			if ( !this.categories[category].mandatory ) {
				return false;
			}
		}
		return true;
	}

	getProviderKeysInCategory( category_key: string ): string[] {
		const keys = [];
		for ( const provider in this.providers ) {
			if (
				!this.providers[provider].categories.includes(
					category_key
				)
			) {
				continue;
			}
			keys.push( provider );
		}
		return keys;
	}

	openCookieManagerUi(): void {
		this.closeCookieBannerUi();
		document.body.classList.add( 'cookie-manager-open' );
	}

	closeCookieManagerUi(): void {
		document.body.classList.remove( 'cookie-manager-open' );
	}

	openCookieBannerUi(): void {
		document.body.classList.add( 'cookie-banner-open' );
	}

	closeCookieBannerUi(): void {
		document.body.classList.remove( 'cookie-banner-open' );
	}

	showButtons(): void {
		document.body.classList.add( 'cookie-manager-buttons-visible' );
	}

	hideButtons(): void {
		document.body.classList.remove( 'cookie-manager-buttons-visible' );
	}

	forceSaveChanges(): void {
		document.body.classList.add( 'cookie-manager-save-changes' );
	}

	removeProviderCookies( providers: CookieProviders ): void {
		for ( const provider in providers ) {
			if ( this.getProviderIsMandatory( provider ) ) {
				continue;
			}
			for ( const cookie in providers[provider].cookies_dropped ) {
				if ( cookie !== 'ten4_cookie_consent' && this.getCookie( cookie ).length > 0 ) {
					this.deleteCookie( cookie );
				}
			}
		}
	}

	injectHtml( configs: CookieProvider[] ): void {
		const html_to_inject = {
			head_end: '',
			body_end: '',
		};
		for ( const config of configs ) {
			if ( typeof config.html !== 'undefined' ) {
				const injection_point = ( config.html_injection_point ?? HTMLInjectionPoint.BodyEnd );
				if ( !Object.prototype.hasOwnProperty.call(
					html_to_inject,
					injection_point
				) ) {
					throw new Error( 'Invalid HTML injection point.' );
				}
				html_to_inject[injection_point] += config.html;
			}
		}
		if ( html_to_inject.head_end.length > 0 ) {
			this.insertHTML( document.head, html_to_inject.head_end );
		}
		if ( html_to_inject.body_end.length > 0 ) {
			this.insertHTML( document.body, html_to_inject.body_end );
		}
	}

	renderProviderWrappers( providers: CookieProviders ) {
		const wrappers: NodeListOf<HTMLElement> = document.querySelectorAll(
			'.cookie-consent-wrapper'
		);
		let unblocked_any = false;
		for ( const wrapper of wrappers ) {
			const provider_key = wrapper.getAttribute( 'data-provider-key' );
			if ( !Object.prototype.hasOwnProperty.call(
				providers,
				provider_key
			) ) {
				continue;
			}
			const wrapper_element = wrapper.querySelector(
				'.cookie-consent-wrapper__unblocked'
			);
			if ( !wrapper_element ) {
				// Already unblocked, probably.
				continue;
			}
			const wrapper_content = wrapper_element.textContent;
			this.insertHTML( wrapper, wrapper_content, true );
			wrapper.classList.add( 'cookie-consent-wrapper--unblocked' );
			unblocked_any = true;
		}

		if ( unblocked_any ) {
			window.dispatchEvent( new CustomEvent( 'cookie-consent:unblocked' ) );
		}
	}

	insertHTML( parent_node: HTMLElement, html_string: string, replace = false ) {
		const fragment_range = document.createRange();
		fragment_range.selectNode( parent_node );
		const fragment = fragment_range.createContextualFragment(
			html_string
		);
		if ( replace ) {
			parent_node.innerHTML = '';
		}
		parent_node.appendChild( fragment );
	}

	getCookie( name: string ): string {
		const value='; ' + document.cookie;
		const parts = value.split( '; ' + name + '=' );
		if ( parts.length === 2 ) {
			return parts.pop().split( ';' ).shift();
		}
		return '';
	}

	setCookie( name: string, value: string, expires: Date ) {
		document.cookie = name + '=' + value + ';path=/;expires=' + expires.toUTCString() + ';SameSite=Lax;Secure';
	}

	deleteCookie( name: string ) {
		const domain = window.location.hostname.split( '.' );
		while ( domain.length > 1 ) {
			document.cookie = name + '=;path=/;domain=' + domain.join( '.' ) + ';expires=Thu, 01 Jan 1970 00:00:01 GMT';
			domain.shift();
		}
	}

	trapFocus( element: HTMLElement ) {
		const focusable_els: NodeListOf<HTMLElement> = element.querySelectorAll(
			'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
		);
		const first_focusable_el = focusable_els[0];
		const last_focusable_el = focusable_els[focusable_els.length - 1];

		if ( !first_focusable_el || !last_focusable_el ) {
			return;
		}

		first_focusable_el.focus();

		element.addEventListener( 'keydown', ( event ) => {
			const tab_pressed = event.key === 'Tab';
			if ( !tab_pressed ) {
				return;
			}
			if ( event.shiftKey ) {
				// Shift + tab (backwards)
				if ( document.activeElement === first_focusable_el ) {
					last_focusable_el.focus();
					event.preventDefault();
				}
			} else {
				// Tab (forwards)
				if ( document.activeElement === last_focusable_el ) {
					first_focusable_el.focus();
					event.preventDefault();
				}
			}
		} );
	}
}
